import Dinero from 'dinero.js'
import { useFormikContext } from 'formik'
import React, { ReactElement, useEffect, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { Grid, Wrapper } from '@farewill/ui'

import RadioGroup from 'components/form/radio-group'
import SelectInput from 'components/form/select-input'
import Input from 'components/form/input'
import { BRAND_ID, FuneralBrandId } from 'config/brands'
import { Constants } from 'lib/models/constants'
import { Country } from 'lib/models/country'
import { Crematorium } from 'lib/models/crematorium'
import {
  AdditionalCharge,
  CoffinType,
  DeceasedLocationType,
} from 'lib/models/funeral'
import {
  COFFINS_WITH_PRICE,
  CREMATION,
  DECEASED_LOCATIONS,
  DELIVER,
  NO_YES_TO_BOOLEAN_OPTIONS,
  SCATTER,
  YES_NO_TO_BOOLEAN_OPTIONS,
  BURIAL,
  TREE_NONE,
  CELEBRATION_NONE,
} from 'utils/enums'
import {
  CountryOption,
  formatAshesInstructionsOptions,
  formatCelebrationOptions,
  formatCountryOptions,
  formatPackageOptions,
  formatTreeOptions,
  formatUrnOptions,
  StringValueFormOption,
} from 'utils/helpers'

Dinero.globalLocale = 'en-GB'
Dinero.defaultCurrency = 'GBP'

export type FuneralQuoteFormFieldsProps = { brandId: FuneralBrandId } & Record<
  string,
  unknown
>

type SaveData = {
  name: string
  value: string
}

type QuestionProps = {
  label: string
  name: string
  hint?: string
  options?: OptionTypes
  disabled?: boolean
  handleSave: (data: SaveData) => void
}

type AnswerComponent = React.ComponentType<QuestionProps>

export type QuoteAttributes = {
  funeralType?: typeof CREMATION | typeof BURIAL
  packageSlug: string
  ashesInstruction: typeof DELIVER | typeof SCATTER
  ashesSplitting?: 'true' | 'false' | null
  ashesSplittingPortions: number | null
  treeType: string | ''
  treePlaqueMessage: string | ''
  urnType: string | '' | null
  urnTypes?: string[]
  attendedService?: 'true' | 'false'
  hasHazardousImplant: string
  deceasedReferredToCoroner?: 'true' | 'false' | null
  coffinType: CoffinType | ''
  deceasedLocationCategory: DeceasedLocationType | ''
  deceasedInMortuary?: 'true' | 'false' | null
  deceasedWeighsOver20Stone?: 'true' | 'false' | null
  additionalCharges: AdditionalCharge[]
  deathCountryId: number | null
  celebrationType?: string | null
  crematoriumId?: number | null
  crematorium?: Crematorium | null
}

type FormValues = {
  quoteAttributes: QuoteAttributes
  isEditableQuote?: boolean
}

type OptionTypes =
  | typeof YES_NO_TO_BOOLEAN_OPTIONS
  | typeof NO_YES_TO_BOOLEAN_OPTIONS
  | typeof COFFINS_WITH_PRICE
  | typeof DECEASED_LOCATIONS
  | StringValueFormOption[]

type QuestionType = {
  name: string
  label: string
  options?: OptionTypes
  component?: JSX.Element
  showIf?: (values: FormValues) => boolean
  disabled?: boolean
  hint?: string
  type?: string
}

type QuestionsType = QuestionType[]

const getQuestions = ({
  brandId,
  formValues,
  packageOptions,
  ashesInstructionsOptions,
  countryOptions,
  celebrationOptions,
  treeOptions,
  firstUrnOptions,
  subsequentUrnOptions,
  isEditableQuote,
  isLeadFuneralPlanRedemption,
  isLeadConverted,
  hasPurchasedCelebration,
}: {
  brandId: FuneralBrandId
  formValues: FormValues
  packageOptions: StringValueFormOption[]
  ashesInstructionsOptions: StringValueFormOption[]
  countryOptions: CountryOption[]
  celebrationOptions: StringValueFormOption[]
  treeOptions: StringValueFormOption[]
  firstUrnOptions: StringValueFormOption[]
  subsequentUrnOptions: StringValueFormOption[]
  isEditableQuote?: boolean
  isLeadFuneralPlanRedemption?: boolean
  isLeadConverted?: boolean
  hasPurchasedCelebration?: boolean
}): QuestionsType => {
  const isFarewill = brandId === BRAND_ID.farewill

  const isDeathCountryScotland =
    countryOptions.find(
      (country: CountryOption) =>
        country.value === formValues?.quoteAttributes?.deathCountryId
    )?.label === 'Scotland'

  const deceasedReferredToCoronerLabel = isDeathCountryScotland
    ? 'Has there been a post-mortem or has the person been referred to the procurator fiscal?'
    : 'Has there been a post-mortem or has the person been referred to the coroner?'

  const urnTypeFirstQuestionLabel =
    formValues?.quoteAttributes?.ashesSplitting === 'true'
      ? 'Which urn would they like for portion 1?'
      : 'Which urn would they like?'

  return [
    {
      name: 'quoteAttributes.packageSlug',
      label: 'Which funeral package would they like?',
      options: packageOptions,
      component: SelectInput,
      disabled:
        isEditableQuote || isLeadFuneralPlanRedemption || isLeadConverted,
    },
    {
      name: 'quoteAttributes.deathCountryId',
      label: 'Where did the person die?',
      options: countryOptions,
      component: SelectInput,
      disabled: isEditableQuote,
    },
    {
      name: 'quoteAttributes.coffinType',
      label: 'What coffin would they prefer?',
      options: COFFINS_WITH_PRICE,
      component: SelectInput,
    },
    {
      name: 'quoteAttributes.celebrationType',
      label: 'Would they like a celebration at the crematorium?',
      options: celebrationOptions,
      component: SelectInput,
      showIf: () => celebrationOptions?.length > 0,
      disabled: hasPurchasedCelebration, // if plan has a celebration, we will not let them cancel it at at-need stage
    },
    {
      name: 'quoteAttributes.treeType',
      label: 'Which tree would they like?',
      options: treeOptions,
      component: SelectInput,
      showIf: () => isFarewill,
    },
    {
      name: 'quoteAttributes.treePlaqueMessage',
      label: 'What would they like the tree plaque to say?',
      showIf: (values: FormValues) =>
        isFarewill &&
        values.quoteAttributes?.treeType &&
        values.quoteAttributes.treeType !== TREE_NONE,
    },
    {
      name: 'quoteAttributes.ashesInstruction',
      label: 'What would they like to do with the ashes?',
      options: ashesInstructionsOptions,
      component: SelectInput,
    },
    {
      name: 'quoteAttributes.ashesSplitting',
      label: 'Would they need the ashes split in 2 or more portions?',
      options: YES_NO_TO_BOOLEAN_OPTIONS,
      showIf: (values: FormValues) =>
        values.quoteAttributes?.ashesInstruction &&
        values.quoteAttributes.ashesInstruction !== SCATTER,
    },
    {
      name: 'quoteAttributes.ashesSplittingPortions',
      label: 'How many portions do they need the ashes to be split into?',
      type: 'number',
      showIf: (values: FormValues) =>
        values.quoteAttributes?.ashesSplitting === 'true',
    },
    {
      name: 'quoteAttributes.urnTypes[0]',
      label: urnTypeFirstQuestionLabel,
      options: firstUrnOptions,
      showIf: (values: FormValues) =>
        'ashesInstruction' in values.quoteAttributes &&
        values.quoteAttributes.ashesInstruction !== SCATTER,
      component: SelectInput,
    },
    {
      name: 'quoteAttributes.urnTypes[1]',
      label: 'Which urn would they like for portion 2?',
      options: subsequentUrnOptions,
      showIf: (values: FormValues) =>
        values.quoteAttributes?.ashesSplitting === 'true',
      component: SelectInput,
    },
    {
      name: 'quoteAttributes.urnTypes[2]',
      label: 'Which urn would they like for portion 3?',
      options: subsequentUrnOptions,
      showIf: (values: FormValues) =>
        values.quoteAttributes?.ashesSplittingPortions &&
        values.quoteAttributes?.ashesSplittingPortions >= 3,
      component: SelectInput,
    },
    {
      name: 'quoteAttributes.urnTypes[3]',
      label: 'Which urn would they like for portion 4?',
      options: subsequentUrnOptions,
      showIf: (values: FormValues) =>
        values.quoteAttributes?.ashesSplittingPortions &&
        values.quoteAttributes?.ashesSplittingPortions >= 4,
      component: SelectInput,
    },
    {
      name: 'quoteAttributes.deceasedLocationCategory',
      label: 'Where are they now?',
      options: DECEASED_LOCATIONS,
      component: SelectInput,
      showIf: (values: FormValues) => !values.isEditableQuote,
    },
    {
      name: 'quoteAttributes.deceasedInMortuary',
      label: 'Do we need to be there urgently?',
      options: NO_YES_TO_BOOLEAN_OPTIONS,
      hint: 'Select ‘No’ if the person is already in a mortuary',
    },
    {
      name: 'quoteAttributes.deceasedReferredToCoroner',
      label: deceasedReferredToCoronerLabel,
      options: YES_NO_TO_BOOLEAN_OPTIONS,
    },
    {
      name: 'quoteAttributes.hasHazardousImplant',
      label:
        'Do they have a pacemaker or other hazardous implant that needs to be removed?',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'not_sure', label: 'Unsure' },
      ],
    },
  ] as QuestionsType
}

const FuneralQuoteFormFields = ({
  brandId,
  isEditableQuote,
  isLeadFuneralPlanRedemption,
  isLeadConverted,
  hasPurchasedCelebration = false,
}: {
  brandId: FuneralBrandId
  isEditableQuote?: boolean
  isLeadFuneralPlanRedemption?: boolean
  isLeadConverted?: boolean
  hasPurchasedCelebration?: boolean
}): ReactElement => {
  const { handleSubmit, setFieldValue, ...context } = useFormikContext()
  const values = context.values as FormValues

  const handleSave = ({ name, value }: SaveData) => {
    /** When package is changed, just wholesale reset the celebrationType and urnTypes */
    if (name === 'quoteAttributes.packageSlug') {
      setFieldValue('quoteAttributes.celebrationType', '')
      setFieldValue('quoteAttributes.urnTypes', [])
    }

    if (name === 'quoteAttributes.ashesInstruction' && value === SCATTER) {
      setFieldValue('quoteAttributes.ashesSplitting', 'false')
      setFieldValue('quoteAttributes.ashesSplittingPortions', '')
      setFieldValue('quoteAttributes.urnType', '')
      setFieldValue('quoteAttributes.urnTypes[0]', '')
      setFieldValue('quoteAttributes.urnTypes[1]', '')
      setFieldValue('quoteAttributes.urnTypes[2]', '')
      setFieldValue('quoteAttributes.urnTypes[3]', '')
    }

    if (name === 'quoteAttributes.deceasedInMortuary' && value === 'false') {
      setFieldValue('quoteAttributes.deceasedReferredToCoroner', 'false')
    }

    if (name === 'quoteAttributes.treeType' && value === TREE_NONE) {
      setFieldValue('quoteAttributes.treePlaqueMessage', '')
    }

    if (name === 'quoteAttributes.ashesSplitting' && value === 'false') {
      setFieldValue('quoteAttributes.ashesSplittingPortions', '')
      setFieldValue('quoteAttributes.urnTypes[1]', '')
      setFieldValue('quoteAttributes.urnTypes[2]', '')
      setFieldValue('quoteAttributes.urnTypes[3]', '')
    }

    if (name === 'quoteAttributes.ashesSplitting' && value === 'true') {
      setFieldValue('quoteAttributes.ashesSplittingPortions', 2)
    }

    if (
      name === 'quoteAttributes.ashesSplittingPortions' &&
      Number(value) < 3
    ) {
      setFieldValue('quoteAttributes.urnTypes[2]', '')
      setFieldValue('quoteAttributes.urnTypes[3]', '')
    }

    if (
      name === 'quoteAttributes.ashesSplittingPortions' &&
      Number(value) < 4
    ) {
      setFieldValue('quoteAttributes.urnTypes[3]', '')
    }

    handleSubmit()
  }

  const cache = useQueryClient()
  const countries = cache.getQueryData('countries') as Country[]
  const countryOptions = formatCountryOptions(countries)

  const constants = cache.getQueryData('constants') as Constants

  const currentPackageSlug = values?.quoteAttributes?.packageSlug
  /** Whether to show the currently selected package read-only rather than
   * treat it as freely re-editable: a funeral case, a plan redemption (the
   * package is inherited from the plan), or an already-converted lead. */
  const shouldShowStalePackage = Boolean(
    isEditableQuote || isLeadFuneralPlanRedemption || isLeadConverted
  )

  const packageOptions = useMemo(
    () =>
      formatPackageOptions({
        brandId,
        isPlanRedemption: !!isLeadFuneralPlanRedemption,
        packages: constants?.attributes?.funeralPackagePrices,
        currentPackageSlug: shouldShowStalePackage
          ? currentPackageSlug
          : undefined,
      }),
    [
      brandId,
      isLeadFuneralPlanRedemption,
      constants?.attributes?.funeralPackagePrices,
      shouldShowStalePackage,
      currentPackageSlug,
    ]
  )

  /** If the package this lead is on is no longer available for new leads,
   * and we're not deliberately keeping it visible read-only, revert the
   * selection to unselected rather than silently keep a stale, no-longer-
   * offered value. */
  useEffect(() => {
    if (
      /** constants can start as something other than undefined (e.g. an
       * empty array) before the query resolves, so check the actual data
       * path it's used for rather than the container's own truthiness. */
      !constants?.attributes?.funeralPackagePrices ||
      !brandId ||
      shouldShowStalePackage ||
      !currentPackageSlug ||
      packageOptions.some((option) => option.value === currentPackageSlug)
    ) {
      return
    }

    setFieldValue('quoteAttributes.packageSlug', '')
  }, [
    constants?.attributes?.funeralPackagePrices,
    brandId,
    shouldShowStalePackage,
    currentPackageSlug,
    packageOptions,
    setFieldValue,
  ])

  const ashesInstructionsOptions = formatAshesInstructionsOptions({
    brandId,
    packageSlug: values?.quoteAttributes?.packageSlug,
    isPlanRedemption: !!isLeadFuneralPlanRedemption,
    prices: constants?.attributes?.ashesDeliveryPrices,
  })
  const treeOptions = formatTreeOptions(constants?.attributes?.treePrices)
  const hasChosenCelebration =
    !!values?.quoteAttributes?.celebrationType &&
    values.quoteAttributes.celebrationType !== CELEBRATION_NONE
  const firstUrnOptions = formatUrnOptions({
    brandId,
    packageSlug: values?.quoteAttributes?.packageSlug,
    urns: constants?.attributes?.urnPrices,
    isFirstUrn: true,
    hasChosenCelebration,
  })
  const subsequentUrnOptions = formatUrnOptions({
    brandId,
    packageSlug: values?.quoteAttributes?.packageSlug,
    urns: constants?.attributes?.urnPrices,
  })
  const celebrationOptions = formatCelebrationOptions({
    packageSlug: values?.quoteAttributes?.packageSlug,
    celebrations: constants?.attributes?.celebrationPrices,
    includeNoneOption: true,
    hasPurchasedCelebration,
  })

  const questions = getQuestions({
    brandId,
    formValues: values,
    packageOptions,
    ashesInstructionsOptions,
    countryOptions,
    celebrationOptions,
    treeOptions,
    firstUrnOptions,
    subsequentUrnOptions,
    isEditableQuote,
    isLeadFuneralPlanRedemption,
    isLeadConverted,
    hasPurchasedCelebration,
  })

  /** A lot of our options now rely on the constants, so let's wait for their retrieval before rendering */
  if (!constants) return <></>

  return (
    <>
      {questions
        .filter(
          ({ showIf }) => !showIf || showIf({ ...values, isEditableQuote })
        )
        .map((question) => {
          const Component = (question.component ??
            RadioGroup) as AnswerComponent

          return (
            <Grid.Item key={question.name}>
              {question.options ? (
                <Component
                  label={question.label}
                  name={question.name}
                  options={question.options}
                  handleSave={handleSave}
                  hint={question.hint}
                  disabled={question.disabled}
                />
              ) : question.type === 'number' ? (
                <Wrapper
                  tag={Input}
                  maxWidth="100px"
                  type="number"
                  label={question.label}
                  name={question.name}
                  handleSave={handleSave}
                />
              ) : (
                <Input
                  label={question.label}
                  name={question.name}
                  handleSave={handleSave}
                  component="textarea"
                />
              )}
            </Grid.Item>
          )
        })}
    </>
  )
}

export default FuneralQuoteFormFields
